import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Snackbar,
  Alert,
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  CircularProgress,
  ListItemAvatar,
  Avatar,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { buildWebviewWebSocketUrl, buildDashboardUnifiedWebSocketUrl } from '../utils/webviewAudio';
import {
  authenticatedFetch,
  getNotificationSettings,
  getNotificationUnreadCount,
  terminateBrowserSession,
} from '../utils/api';
import { subscribeDashboardWebSocket, DASHBOARD_WS_CONNECT_DELAYS } from '../utils/dashboardWebSocketManager';
import { getOrFetch as fetchAuditImage, peek as peekAuditImage } from '../services/auditImageCache';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/dateUtils';
import { useGlobalNotificationUi } from '../contexts/GlobalNotificationUiContext';


// Dark Mode color - ( All same black)
const TOAST_COLORS_3 = {
  unmatched: { bg: '#050404d4', text: '#f70000', border: '#f70000' },
  possible_match: { bg: '#050404d4', text: '#f97316', border: '#f97316' },
  check_out: { bg: '#050404d4', text: '#ffffff', border: '#ffffff' },
  check_in: { bg: '#050404d4', text: '#20b355', border: '#20b355' },
};

const TOAST_COLORS = TOAST_COLORS_3

const DEFAULT_TOAST_DURATION_MS = 3000;
const DEFAULT_NOTIFICATION_SETTINGS = {
  notifications_enabled: true,
  show_check_in: true,
  show_check_out: true,
  show_possible_match: true,
  show_unmatched: true,
  toast_duration_seconds: null,
};

const stripTrailingUtcTimestamp = (text) => {
  if (!text) return text;
  return String(text).replace(/\s+at\s+\d{4}-\d{2}-\d{2}T[\d:.]+Z?$/i, '');
};

const formatActionLabel = (action) => {
  const a = String(action || '').toLowerCase();
  if (a === 'check_out') return 'checked out';
  if (a === 'check_in') return 'checked in';
  return a.replace(/_/g, ' ');
};

const formatPossibleMatchMessage = (name, camera) => {
  const person = name || 'Unknown';
  const label = String(camera || '').trim().toLowerCase();
  if (label === 'in' || label.startsWith('in')) {
    return `Possibly ${person} checked in`;
  }
  if (label === 'out' || label.startsWith('out')) {
    return `Possibly ${person} checked out`;
  }
  return `Possibly ${person} detected`;
};

const getNotificationPrimary = (n) => {
  const status = String(n?.event_status || '').toLowerCase();
  if (status === 'possible_match' || status === 'unmatched') {
    const hasTransition = String(n?.message_text || '').includes('->');
    if (hasTransition) return n.message_text;
    return status === 'possible_match'
      ? formatPossibleMatchMessage(n.person_name, n.camera)
      : 'Unknown person found.';
  }
  if (n?.person_name && n?.action) {
    return `${n.person_name} ${formatActionLabel(n.action)}`;
  }
  if (n?.message_text) {
    return stripTrailingUtcTimestamp(n.message_text);
  }
  if (n?.person_name) {
    return n.person_name;
  }
  return 'Notification';
};

const shouldShowNotification = (n) => {
  const source = String(n?.source_event || '').toLowerCase();
  const status = String(n?.event_status || '').toLowerCase();
  const hasTransitionHistory = Array.isArray(n?.status_history) || String(n?.message_text || '').includes('->');
  return !(source === 'recognition' && status === 'matched' && !hasTransitionHistory);
};

const normalizeNotificationStatus = (value) => {
  const status = String(value || '').trim().toLowerCase();
  if (['possible_match', 'possible-match', 'possiblematch'].includes(status)) return 'possible_match';
  if (['unmatched', 'unmatch', 'no_match', 'no-match', 'nomatch', 'unknown', 'unknown_face', 'unmatched_face'].includes(status)) {
    return 'unmatched';
  }
  return status;
};

const getNotificationType = (n = {}) => {
  const status = normalizeNotificationStatus(n?.event_status || n?.alert_type || n?.confidence || n?.confidence_level);
  const action = String(n?.action || '').toLowerCase();
  if (status === 'possible_match') return 'possible_match';
  if (status === 'unmatched') return 'unmatched';
  if (action === 'check_in') return 'check_in';
  if (action === 'check_out') return 'check_out';
  return null;
};

const transitionHasStatus = (n = {}, status) => {
  const target = normalizeNotificationStatus(status);
  const history = Array.isArray(n?.status_history) ? n.status_history : [];
  if (history.some((item) => normalizeNotificationStatus(item?.status) === target)) {
    return true;
  }
  const text = String(n?.message_text || '').toLowerCase();
  if (target === 'possible_match') return text.includes('possible match:');
  if (target === 'unmatched') return text.includes('unknown person found');
  return false;
};

const isNotificationAllowed = (n, settings = DEFAULT_NOTIFICATION_SETTINGS) => {
  if (!settings?.notifications_enabled) return false;
  const source = String(n?.source_event || '').toLowerCase();
  const status = normalizeNotificationStatus(n?.event_status || n?.alert_type || n?.confidence || n?.confidence_level);
  const hasTransitionHistory = Array.isArray(n?.status_history) || String(n?.message_text || '').includes('->');
  if (source === 'recognition' && status === 'matched' && hasTransitionHistory) {
    return (
      (transitionHasStatus(n, 'possible_match') && settings.show_possible_match !== false) ||
      (transitionHasStatus(n, 'unmatched') && settings.show_unmatched !== false)
    );
  }
  const type = getNotificationType(n);
  if (type === 'check_in') return settings.show_check_in !== false;
  if (type === 'check_out') return settings.show_check_out !== false;
  if (type === 'possible_match') return settings.show_possible_match !== false;
  if (type === 'unmatched') return settings.show_unmatched !== false;
  return true;
};

const getNotificationSecondary = (n) => {
  const parts = [formatDate(n?.created_at, true, true, false, false)];
  if (n?.camera) parts.push(`Camera: ${n.camera}`);
  // if (n?.track_id !== undefined && n?.track_id !== null && String(n.track_id).trim()) {
  //   parts.push(`Track: ${n.track_id}`);
  // }
  return parts.filter(Boolean).join(' • ');
};

const getNotificationStyles = (n) => {
  const status = String(n?.event_status || '').toLowerCase();
  const action = String(n?.action || '').toLowerCase();
  const camera = String(n?.camera || '').toLowerCase();

  // 1. Unmatched (Red)
  if (status === 'unmatched') {
    return {
      color: '#ffffff',
      bgColor: '#d32f2f',
      Icon: ErrorIcon,
    };
  }
  // 2. Possible Match (Orange)
  if (status === 'possible_match') {
    return {
      color: '#f97316',
      bgColor: '#ffedd5',
      Icon: WarningIcon,
    };
  }
  // 3. Check-Out (red)
  if (action === 'check_out' || camera === 'out' || camera.includes('out')) {
    return {
      color: '#991b1b',
      bgColor: '#fee2e2',
      Icon: LogoutIcon,
    };
  }
  // 4. Check-In / Matched (Green)
  if (action === 'check_in' || camera === 'in' || camera.includes('in') || status === 'matched') {
    return {
      color: '#166534',
      bgColor: '#dcfce7',
      Icon: LoginIcon,
    };
  }

  // Fallback (Gray)
  return {
    color: '#6b7280',
    bgColor: '#f3f4f6',
    Icon: NotificationsIcon,
  };
};

const resolveAuditImageUrl = (auditId, imageCache) => {
  if (!auditId) return undefined;
  if (imageCache[auditId]) return imageCache[auditId];
  const cached = peekAuditImage(auditId);
  return typeof cached === 'string' ? cached : undefined;
};

const MAX_VISIBLE_TOASTS = 4;
const TOAST_OFFSET_PX = 64;

const GlobalAttendanceToast = () => {
  const navigate = useNavigate();
  const { setBellVisible } = useGlobalNotificationUi();
  const websocketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const [toasts, setToasts] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOffset, setNotifOffset] = useState(0);
  const [notifHasMore, setNotifHasMore] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [imageCache, setImageCache] = useState({});
  const [notificationSettings, setNotificationSettings] = useState(DEFAULT_NOTIFICATION_SETTINGS);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const imageCacheRef = useRef({});
  const failedImageIdsRef = useRef(new Set());
  const notificationsListLoadedRef = useRef(false);
  const PAGE_SIZE = 10;

  const toastDurationMs = notificationSettings.toast_duration_seconds
    ? Number(notificationSettings.toast_duration_seconds) * 1000
    : DEFAULT_TOAST_DURATION_MS;

  const isAttendanceEvent = useCallback((payload = {}) => {
    const eventType = String(payload.event || '').toLowerCase();
    const mode = String(payload.mode || '').toLowerCase();
    return eventType === 'attendance' || eventType === 'recognition_alert' || mode === 'attendance';
  }, []);

  const pushToast = useCallback((toast) => {
    setToasts((prev) => [
      ...prev,
      {
        id: `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        ...toast,
      },
    ].slice(-MAX_VISIBLE_TOASTS));
  }, []);

  const handleToastClose = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showLiveToast = useCallback((payload = {}) => {
    if (!isNotificationAllowed(payload, notificationSettings)) return;

    const eventType = String(payload.event || '').toLowerCase();
    const eventStatus = normalizeNotificationStatus(
      payload.event_status || payload.alert_type || payload.confidence || payload.confidence_level
    );
    const action = String(payload.action || '').toLowerCase();
    const isRecognitionTransitionMatch = (
      eventStatus === 'matched' &&
      String(payload.source_event || '').toLowerCase() === 'recognition' &&
      (Array.isArray(payload.status_history) || String(payload.message_text || '').includes('->'))
    );
    if (eventStatus === 'possible_match' || eventStatus === 'unmatched' || isRecognitionTransitionMatch) {
      const baseMessage = payload.message_text || (eventStatus === 'possible_match'
        ? formatPossibleMatchMessage(payload.person_name, payload.camera)
        : (eventStatus === 'matched' ? `Matched: ${payload.person_name || 'Unknown'}` : 'Unknown person found.'));
      const cameraLabel = payload.camera ? ` (${payload.camera})` : '';
      const message = `${baseMessage}${cameraLabel}`;
      const toastPalette = eventStatus === 'possible_match'
        ? TOAST_COLORS.possible_match
        : (eventStatus === 'unmatched' ? TOAST_COLORS.unmatched : null);
      pushToast({
        message,
        severity: eventStatus === 'possible_match' ? 'warning' : (eventStatus === 'unmatched' ? 'error' : 'success'),
        toastColor: toastPalette?.bg || null,
        toastTextColor: toastPalette?.text || null,
        toastBorderColor: toastPalette?.border || null,
        type: eventStatus,
      });
      setUnreadCount((prev) => prev + 1);
      setNotifications((prev) => [
        ...(() => {
          const nextItem = {
          notification_id: payload.notification_id || `live-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          message_text: baseMessage,
          action: payload.action || null,
          event_status: eventStatus,
          source_event: payload.source_event || payload.event || 'recognition',
          audit_id: payload.audit_id || null,
          camera: payload.camera || null,
          track_id: payload.track_id || null,
          stack_size: payload.stack_size || 1,
          status_history: payload.status_history || null,
          created_at: payload.timestamp || payload.created_at || new Date().toISOString(),
          };
          if (!payload.notification_id) return [nextItem, ...prev];
          const withoutExisting = prev.filter((item) => item.notification_id !== payload.notification_id);
          return [nextItem, ...withoutExisting];
        })(),
      ].slice(0, 200));
      return;
    }

    if (eventType === 'recognition') return;

    if (action !== 'check_in' && action !== 'check_out') return;
    const person = payload.person && typeof payload.person === 'object' ? payload.person : {};
    const name = person.name || payload.person_name || 'Unknown';
    const actionLabel = action === 'check_out' ? 'Checked Out' : 'Checked In';
    const toastPalette = action === 'check_out' ? TOAST_COLORS.check_out : TOAST_COLORS.check_in;
    pushToast({
      message: `${name} ${actionLabel}`,
      severity: 'success',
      toastColor: toastPalette.bg,
      toastTextColor: toastPalette.text,
      toastBorderColor: toastPalette.border || null,
      type: action,
    });
    setUnreadCount((prev) => prev + 1);
    const ts = payload.timestamp || payload.created_at || new Date().toISOString();
    const messageText = `${name} ${actionLabel}`;
    setNotifications((prev) => [
      {
        notification_id: `live-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        message_text: messageText,
        action,
        event_status: 'matched',
        source_event: payload.event || payload.mode || 'attendance',
        audit_id: payload.audit_id || null,
        created_at: ts,
      },
      ...prev,
    ].slice(0, 200));
  }, [notificationSettings, pushToast]);

  const loadNotifications = useCallback(async ({ offset = 0, append = false } = {}) => {
    if (!notificationSettings.notifications_enabled) {
      setNotifications([]);
      setUnreadCount(0);
      setNotifOffset(0);
      setNotifHasMore(false);
      notificationsListLoadedRef.current = false;
      return;
    }
    setNotifLoading(true);
    try {
      const response = await authenticatedFetch(`/auth/admin/notifications?limit=${PAGE_SIZE}&offset=${offset}`);
      if (!response.ok) return;
      const data = await response.json();
      const list = Array.isArray(data?.notifications)
        ? data.notifications.filter((n) => shouldShowNotification(n) && isNotificationAllowed(n, notificationSettings))
        : [];
      setNotifications((prev) => (append ? [...prev, ...list] : list));
      setNotifOffset(offset + list.length);
      setNotifHasMore(Boolean(data?.has_more));
      if (!append) {
        notificationsListLoadedRef.current = true;
      }
    } catch (err) {
      console.error('[GlobalAttendanceToast] Failed to load notifications:', err);
    } finally {
      setNotifLoading(false);
    }
  }, [notificationSettings]);

  const loadUnreadCount = useCallback(async () => {
    if (!notificationSettings.notifications_enabled) {
      setUnreadCount(0);
      return;
    }
    try {
      const data = await getNotificationUnreadCount();
      setUnreadCount(Number(data?.unread_count || 0));
    } catch (err) {
      console.error('[GlobalAttendanceToast] Failed to load unread count:', err);
    }
  }, [notificationSettings.notifications_enabled]);

  const markNotificationsRead = useCallback(async () => {
    if (!notificationSettings.notifications_enabled) return;
    try {
      const response = await authenticatedFetch('/auth/admin/notifications/mark-read', {
        method: 'POST',
      });
      if (!response.ok) return;
      const data = await response.json();
      setUnreadCount(Number(data?.unread_count || 0));
    } catch (err) {
      console.error('[GlobalAttendanceToast] Failed to mark notifications read:', err);
    }
  }, [notificationSettings.notifications_enabled]);

  const bellOpen = Boolean(anchorEl);

  useEffect(() => {
    if (!bellOpen) return undefined;

    const syncedFromModule = {};
    notifications.forEach((n) => {
      const id = (n.audit_id || '').trim();
      if (!id) return;
      const cached = peekAuditImage(id);
      if (typeof cached === 'string') syncedFromModule[id] = cached;
    });
    if (Object.keys(syncedFromModule).length > 0) {
      setImageCache((prev) => {
        const next = { ...prev, ...syncedFromModule };
        imageCacheRef.current = next;
        return next;
      });
    }

    const ids = notifications
      .map((n) => (n.audit_id || '').trim())
      .filter((id) => id && peekAuditImage(id) === undefined && !failedImageIdsRef.current.has(id));
    if (ids.length === 0) return undefined;

    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        ids.map(async (id) => {
          try {
            const url = await fetchAuditImage(id);
            if (url === null) {
              failedImageIdsRef.current.add(id);
              return null;
            }
            return [id, url];
          } catch (err) {
            failedImageIdsRef.current.add(id);
            return null;
          }
        })
      );
      if (cancelled) return;
      setImageCache((prev) => {
        const next = { ...prev };
        entries.forEach((e) => {
          if (e) next[e[0]] = e[1];
        });
        imageCacheRef.current = next;
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [notifications, bellOpen]);

  useEffect(() => () => {
    setImageCache({});
    imageCacheRef.current = {};
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const data = await getNotificationSettings();
      setNotificationSettings({ ...DEFAULT_NOTIFICATION_SETTINGS, ...(data?.settings || {}) });
    } catch (err) {
      console.error('[GlobalAttendanceToast] Failed to load notification settings:', err);
      setNotificationSettings(DEFAULT_NOTIFICATION_SETTINGS);
    } finally {
      setSettingsLoaded(true);
    }
  }, []);


  useEffect(() => {
    loadSettings();
    const onSettingsUpdated = () => loadSettings();
    window.addEventListener('notification-settings-updated', onSettingsUpdated);
    return () => window.removeEventListener('notification-settings-updated', onSettingsUpdated);
  }, [loadSettings]);


  useEffect(() => {
    const visible = settingsLoaded && notificationSettings.notifications_enabled;
    setBellVisible(visible);
    return () => setBellVisible(false);
  }, [notificationSettings.notifications_enabled, settingsLoaded, setBellVisible]);


  useEffect(() => {
    if (!settingsLoaded) return undefined;
    if (!notificationSettings.notifications_enabled) {
      setToasts([]);
      setNotifications([]);
      setUnreadCount(0);
      setNotifOffset(0);
      setNotifHasMore(false);
      notificationsListLoadedRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (websocketRef.current) {
        try { websocketRef.current.close(); } catch (e) { /* ignore */ }
        websocketRef.current = null;
      }
      return undefined;
    }

    loadUnreadCount();
    let disposed = false;
    const connectWebsocket = () => {
      if (disposed) return;
      try {
        if (websocketRef.current) {
          websocketRef.current.close();
        }
        const wsUrl = buildWebviewWebSocketUrl(null, false);
        const socket = new WebSocket(wsUrl);
        websocketRef.current = socket;

        socket.onmessage = (event) => {
          try {
            const payload = JSON.parse(event.data);
            if (!isAttendanceEvent(payload)) return;
            showLiveToast(payload);
          } catch (err) {
            console.error('[GlobalAttendanceToast WS] Failed to parse message:', err);
          }
        };

        socket.onclose = (event) => {
          websocketRef.current = null;
          if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
          }
          if (disposed) return;
          if (event.code === 4403) {
            terminateBrowserSession(
              'ACCOUNT_INACTIVE',
              'Your account has been deactivated. Contact your administrator.'
            );
            return;
          }
          if (event.code === 4401) {
            terminateBrowserSession(
              'SESSION_REVOKED',
              'Your session is no longer active. Please sign in again.'
            );
            return;
          }
          reconnectTimerRef.current = setTimeout(connectWebsocket, 4000);
        };

        socket.onerror = () => {
          try { socket.close(); } catch (e) { /* ignore */ }
        };
      } catch (error) {
        console.error('[GlobalAttendanceToast WS] Failed to initialise connection:', error);
      }
    };

    connectWebsocket();

    return () => {
      disposed = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (websocketRef.current) {
        websocketRef.current.onclose = null;
        try { websocketRef.current.close(); } catch (e) { /* ignore */ }
        websocketRef.current = null;
      }
    };
  }, [settingsLoaded, notificationSettings.notifications_enabled, isAttendanceEvent, showLiveToast, loadUnreadCount]);

  const handleBellClick = async (event) => {
    if (anchorEl){
      setAnchorEl(null);
      return;
    }
    setAnchorEl(event.currentTarget);
    if (!notificationsListLoadedRef.current) {
      await loadNotifications({ offset: 0, append: false });
    }
    await markNotificationsRead();
  };

  const handleBellClose = () => {
    setAnchorEl(null);
  };

  const handleShowMore = async () => {
    if (notifLoading || !notifHasMore) return;
    await loadNotifications({ offset: notifOffset, append: true });
  };

  // audit_id present → exact deep-link to the single audit record. Notifications
  // without an audit_id are not navigable.
  const handleNotificationClick = useCallback((n) => {
    setAnchorEl(null);
    const auditId = (n.audit_id || '').trim();
    if (!auditId) return;
    navigate(`/in-out?audit_id=${encodeURIComponent(auditId)}&view=logs`);
  }, [navigate]);



  if (!settingsLoaded || !notificationSettings.notifications_enabled) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: { xs: 12, md: 24 },
          right: { xs: 18, md: 28 },
          zIndex: (theme) => theme.zIndex.snackbar + 2,
        }}
      >
        <IconButton
          onClick={handleBellClick}
          sx={{
            bgcolor: '#ffffff',
            border: '1px solid #e5e7eb',
            boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
            '&:hover': { bgcolor: '#f9fafb' },
          }}
        >
          <Badge color="error" variant="dot" invisible={unreadCount <= 0} sx={{
          '& .MuiBadge-badge': {
            width: 12,
            height: 12,
            minWidth: 12,
            borderRadius: '50%',
          },
        }}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Box>

      <Popover
        open={bellOpen}
        anchorEl={anchorEl}
        onClose={handleBellClose}
        disableScrollLock={true}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ zIndex: (theme) => theme.zIndex.snackbar + 2 }}
        slotProps={{ paper: { sx: { mt: 0.7, borderRadius: 2 } } }}
      >
        <Box sx={{ width: 380, maxWidth: '92vw', maxHeight: 520, overflowY: 'auto' }}>
          <Box sx={{ p: 1.5, borderBottom: '1px solid #e5e7eb' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontFamily: 'Poppins, Arial, sans-serif'}}>
              Notifications
            </Typography>
          </Box>
          <List dense disablePadding>
            {notifications.length === 0 ? (
              <ListItem>
                <ListItemText primary="No notifications yet." />
              </ListItem>
            ) : (
              notifications.map((n, idx) => {
                const styles = getNotificationStyles(n);
                const auditId = (n.audit_id || '').trim();
                const isClickable = Boolean(auditId);
                const imageUrl = resolveAuditImageUrl(auditId, imageCache);
                const hasTransition = (Array.isArray(n?.status_history) && n.status_history.length > 1)
                  || String(n?.message_text || '').includes('->');
                return (
                  <React.Fragment key={n.notification_id || `${n.created_at}-${idx}`}>
                    <ListItem
                      alignItems="center"
                      button={isClickable}
                      onClick={isClickable ? () => handleNotificationClick(n) : undefined}
                      sx={{
                        py: 1.25,
                        px: 2,
                        cursor: isClickable ? 'pointer' : 'default',
                        transition: 'background-color 0.2s',
                        ...(hasTransition && {
                          borderLeft: '4px solid #D32F2F',
                        }),
                        '&:hover': {
                          bgcolor: isClickable ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                        }
                      }}
                    >
                      <ListItemAvatar
                        sx={{
                          minWidth: 0,
                          mr: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Avatar
                          src={imageUrl}
                          imgProps={{ style: { objectFit: 'cover' } }}
                          sx={{
                            width: 38,
                            height: 38,
                            bgcolor: styles.bgColor,
                            color: styles.color,
                            borderRadius: '50%',
                            border: `2.5px solid ${styles.bgColor}`,
                          }}
                        >
                          <styles.Icon sx={{ fontSize: 18 }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={(
                          <Box component="span">
                            {getNotificationPrimary(n).replace(/\s*->\s*/g, ' → ')}
                            {/* <styles.Icon
                              sx={{
                                fontSize: 16,
                                color: styles.color,
                                verticalAlign: 'middle',
                                position: 'relative',
                                top: '-1px',
                                ml: 2,
                              }}
                            /> */}
                          </Box>
                        )}
                        secondary={getNotificationSecondary(n)}
                        primaryTypographyProps={{
                          component: 'div',
                          sx: { fontWeight: 600, fontSize: '0.875rem', color: '#1f2937' }
                        }}
                        secondaryTypographyProps={{
                          sx: { fontSize: '0.75rem', color: '#6b7280', mt: 0.25 }
                        }}
                      />
                    </ListItem>
                    {idx < notifications.length - 1 ? <Divider /> : null}
                  </React.Fragment>
                );
              })
            )}
          </List>
          <Box sx={{ p: 1.25, borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'center' }}>
            {notifLoading ? (
              <CircularProgress size={20} />
            ) : notifHasMore ? (
              <Button size="small" variant="text" onClick={handleShowMore}>
                Show more
              </Button>
            ) : (
              <Typography variant="caption" color="text.secondary">
                End of notifications
              </Typography>
            )}
          </Box>
        </Box>
      </Popover>

      {toasts.map((t, index) => (
        <Snackbar
          key={t.id}
          open
          autoHideDuration={toastDurationMs}
          onClose={(event, reason) => {
            if (reason === 'clickaway') return;
            handleToastClose(t.id);
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          sx={{
            bottom: { xs: 12 + index * TOAST_OFFSET_PX, md: 16 + index * TOAST_OFFSET_PX },
            left: { xs: 18, md: 28 },
            right: 'auto',
            top: 'auto',
            transition: 'bottom 0.3s ease',
            zIndex: (theme) => theme.zIndex.snackbar + 3 + index,
          }}
          style={{
            width:"30%"
          }}
        >
          <Alert
            onClose={() => handleToastClose(t.id)}
            severity={t.severity}
            variant="filled"
            sx={{
              width: '100%',
              ...(t.toastColor && {
                bgcolor: t.toastColor,
                color: t.toastTextColor || '#fff',
                '& .MuiAlert-icon': {
                  color: t.toastTextColor || '#fff',
                },
              }),
              ...(t.toastBorderColor && {
                border: `1px solid ${t.toastBorderColor}`,
              }),
            }}
          >
            {t.message}
          </Alert>
        </Snackbar>
      ))}
    </>
  );
};

export default GlobalAttendanceToast;
